import { createContext, useContext } from "react";

/**
 * The handle every onboarding screen has on the assistant sheet.
 *
 * Split out of `assistant-sheet.tsx` so the direction of the imports stays
 * one-way: the sheet is built from the shared pieces in `components.tsx`
 * (the pill CTA, the escalation notice), and those same pieces open the
 * assistant. Screens depend on this file; only the sheet itself depends on
 * the sheet.
 */

export interface OpenOptions {
  stepKey?: string | null;
  stage?: string | null;
  /** Pre-filled question, sent immediately — used by the stuck-state buttons. */
  seed?: string;
  /**
   * Pre-filled question left in the composer, not sent. For the moments a
   * resident should attach a photo before the question goes: the cable
   * screen's "Not sure which socket? Ask with a photo".
   */
  draft?: string;
  /** The error the screen was showing, passed to the backend for context. */
  lastError?: string | null;
  /**
   * What the resident does on this screen to carry on once the chat has
   * recorded a correction for VELUX (a `note_for_velux` reply). Named by the
   * screen, since only it knows its own button: "Tap The numbers match now".
   * Pinned under the thread by the app, so the way forward never depends on
   * the model naming the button.
   */
  carryOn?: string;
}

export interface AssistantApi {
  open: (options?: OpenOptions) => void;
}

export const AssistantContext = createContext<AssistantApi | null>(null);

/**
 * Access the assistant from any onboarding screen.
 *
 * Returns a no-op `open` outside the provider so a screen can be rendered in
 * isolation (the dev gallery, tests) without crashing.
 */
export function useAssistant(): AssistantApi {
  return useContext(AssistantContext) ?? { open: () => undefined };
}

/**
 * The flow's own skip, as the escalation card calls it.
 *
 * Resolves to whether the household actually moved on. The shell declines a
 * skip that is already in flight, and turns a failed one into an error of its
 * own, so a caller that dismisses itself on the way out has to be told which
 * of the two happened. Typed here rather than in the sheet because it is the
 * contract between the shell and the sheet, and both ends read this file.
 */
export type SkipStepHandler = (stepKey: string, reason: string) => boolean | Promise<boolean>;
